import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

export default function GiaccheArticle() {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Itaobuy Spreadsheet Giacche: Bomber, Denim Jacket e Giubbotti con Foto QC',
    description: 'Scopri le migliori giacche su Itaobuy Spreadsheet: bomber, giubbotti in denim, giacche tecniche e modelli streetwear. Guida italiana con foto QC e prezzi aggiornati.',
    author: { '@type': 'Organization', name: 'Itaobuy Spread' },
    datePublished: '2026-07-14',
    inLanguage: 'it',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Che tipo di giacche posso trovare su Itaobuy Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Bomber jacket, denim jacket, giacche tecniche, giubbotti invernali, trench e modelli streetwear. Ogni giacca include foto QC dettagliate.' } },
      { '@type': 'Question', name: 'Come valutare la qualità di una giacca con le foto QC?', acceptedAnswer: { '@type': 'Answer', text: 'Controlla il materiale esterno, la fodera interna, la qualità di cerniere e bottoni, le cuciture su spalle e maniche e i dettagli delle tasche nelle foto ravvicinate.' } },
      { '@type': 'Question', name: 'Itaobuy Spreadsheet ha giacche per ogni stagione?', acceptedAnswer: { '@type': 'Answer', text: 'Sì, troverai giacche leggere per la primavera, bomber per l\'autunno, giubbotti pesanti per l\'inverno e modelli versatili per tutto l\'anno.' } },
    ],
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.mom';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Itaobuy Spreadsheet Giacche', item: `${siteUrl}/itaobuy-spreadsheet-giacche` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-20">
        <article className="px-6 md:px-10 max-w-4xl mx-auto py-10 md:py-16">
          <nav className="text-xs text-warm-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-tealwarm-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-warm-600">Itaobuy Spreadsheet Giacche</span>
          </nav>

          <header>
            <span className="inline-block px-3 py-1 rounded-full bg-tealwarm-100 text-tealwarm-700 text-xs font-semibold uppercase tracking-wide mb-4">
              Categoria Giacche
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-warm-950 leading-tight">
              <span className="text-tealwarm-600">Itaobuy Spreadsheet Giacche:</span> Bomber, Denim e Giubbotti
            </h1>
            <p className="mt-4 text-sm md:text-base text-warm-600 leading-relaxed">
              Che tu stia cercando un bomber streetwear, una classica denim jacket o un giubbotto tecnico per l&apos;inverno, <strong>itaobuy spreadsheet giacche</strong> è la risorsa che fa per te. Ogni giacca è documentata con foto QC che mostrano materiali, fodere, zip e dettagli costruttivi — tutto ciò che conta davvero in un capospalla di qualità.
            </p>
          </header>

          <div className="mt-10">
            <img
              src="https://readdy.ai/api/search-image?query=Three%20stylish%20jackets%20a%20vintage%20denim%20trucker%20a%20olive%20nylon%20bomber%20and%20a%20camel%20wool%20coat%20displayed%20on%20individual%20wooden%20hangers%20against%20a%20textured%20plaster%20wall%20in%20warm%20beige%2C%20soft%20gallery%20lighting%20from%20above%2C%20boutique%20fashion%20display%20aesthetic%2C%20clean%20editorial%20photography%20with%20dramatic%20shadows&width=1200&height=600&seq=article-giacche-hero&orientation=landscape"
              alt="Itaobuy Spreadsheet Giacche bomber"
              title="Itaobuy Spreadsheet Giacche"
              className="w-full rounded-2xl"
            />
          </div>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              I migliori tipi di <span className="text-tealwarm-600">giacche</span> sullo spreadsheet
            </h2>
            <p className="mt-4 text-sm md:text-base text-warm-700 leading-relaxed">
              L&apos;<strong>itaobuy spreadsheet</strong> organizza le giacche in modo chiaro, con foto QC che mostrano ogni angolazione. Puoi confrontare bomber in nylon, denim jacket con lavaggi vintage, giacche tecniche con membrane impermeabili e trench eleganti — tutto nella stessa pagina, con link diretti per approfondire.
            </p>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-warm-50 rounded-2xl p-5">
                <h3 className="font-bold text-warm-900 flex items-center gap-2"><i className="ri-flight-takeoff-line text-tealwarm-600"></i> Bomber Jacket</h3>
                <p className="mt-1 text-sm text-warm-600">Modelli streetwear con zip frontale, tasche laterali e interno trapuntato. Controlla la lucentezza del nylon e la qualità della zip nelle foto QC.</p>
              </div>
              <div className="bg-warm-50 rounded-2xl p-5">
                <h3 className="font-bold text-warm-900 flex items-center gap-2"><i className="ri-shirt-line text-tealwarm-600"></i> Denim Jacket</h3>
                <p className="mt-1 text-sm text-warm-600">Classici giubbotti in jeans con lavaggi acid wash, vintage o raw. Verifica lo spessore del denim e i bottoni metallici.</p>
              </div>
              <div className="bg-warm-50 rounded-2xl p-5">
                <h3 className="font-bold text-warm-900 flex items-center gap-2"><i className="ri-cloud-windy-line text-tealwarm-600"></i> Giacca Tecnica</h3>
                <p className="mt-1 text-sm text-warm-600">Modelli funzionali antivento e impermeabili. Controlla cuciture termosaldate e cappuccio regolabile nelle foto QC.</p>
              </div>
              <div className="bg-warm-50 rounded-2xl p-5">
                <h3 className="font-bold text-warm-900 flex items-center gap-2"><i className="ri-user-star-line text-tealwarm-600"></i> Trench e Cappotti</h3>
                <p className="mt-1 text-sm text-warm-600">Capi eleganti in lana o misto cotone. Verifica la fodera interna, le spalline e la qualità dei bottoni.</p>
              </div>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">
              Checklist: come valutare una <span className="text-tealwarm-600">giacca</span>
            </h2>
            <div className="mt-5 space-y-3">
              {['Controlla la zip principale: deve scorrere fluida senza incepparsi, con denti ben allineati.',
                'Verifica le cuciture su spalle e maniche: devono essere dritte e rinforzate nei punti di tensione.',
                'Ispeziona la fodera interna: un tessuto di qualità e cuciture pulite fanno la differenza nella durata.',
                'Osserva tasche e bottoni: le asole devono essere ben rifinite, i bottoni saldamente fissati.',
                'Confronta il colore reale nelle foto QC con le immagini promozionali per valutare la fedeltà.',
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-tealwarm-100 flex items-center justify-center text-tealwarm-700 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs"></i>
                  </div>
                  <p className="text-sm text-warm-700 leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-warm-950">Domande frequenti</h2>
            <div className="mt-5 space-y-4">
              {[
                { q: 'Che tipo di giacche posso trovare su Itaobuy Spreadsheet?', a: 'Bomber jacket, denim jacket, giacche tecniche, giubbotti invernali, trench e modelli streetwear. Ogni giacca include foto QC dettagliate.' },
                { q: 'Come valutare la qualità di una giacca con le foto QC?', a: 'Controlla il materiale esterno, la fodera, la qualità di cerniere e bottoni, le cuciture su spalle e maniche e i dettagli delle tasche.' },
                { q: 'Itaobuy Spreadsheet ha giacche per ogni stagione?', a: 'Sì, troverai giacche leggere per primavera, bomber per autunno, giubbotti pesanti per inverno e modelli versatili tutto l\'anno.' },
              ].map((item, i) => (
                <div key={i} className="bg-cream-50 rounded-2xl p-5">
                  <h3 className="font-bold text-warm-900">{item.q}</h3>
                  <p className="mt-2 text-sm text-warm-600 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-12 bg-gradient-to-r from-tealwarm-50 to-cream-50 rounded-2xl p-8 text-center">
            <h2 className="font-display text-2xl font-bold text-warm-950">
              Esplora le <span className="text-tealwarm-600">giacche</span> su Itaobuy Sheet
            </h2>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-warm-900 text-white text-sm font-semibold hover:bg-warm-800 transition-all whitespace-nowrap cursor-pointer">
                Giacche su Itaobuy Sheet <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link to="/" className="text-sm text-tealwarm-700 hover:text-tealwarm-800 font-medium transition-colors">
                <i className="ri-arrow-left-line mr-1"></i> Torna alla homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}